/**
 * Generic Super Admin bulk-operation results model.
 * Reusable across Songs, Albums, Artists, Record Labels, etc.
 * Session-scoped display only — not a durable work queue.
 */

import { scheduleSideLabel } from "@/features/channels/scheduleSideDisplay";

export type BulkOperationFailureCategory =
  | "continuity-blocked"
  | "channel-cleanup-incomplete"
  | "programming-references-remain"
  | "dependency"
  | "auth"
  | "not-found"
  | "conflict"
  | "network"
  | "other";

/** One failed row in a bulk operation. */
export interface BulkOperationFailure {
  id: string;
  /** First identity column (Songs: Artist; catalog entities: Name). */
  first: string | null;
  /** Second identity column (Songs: Title; omit/null for single-name entities). */
  second: string | null;
  /** Optional secondary id display (e.g. TR-…), tooltip/details only. */
  idLabel?: string | null;
  category: BulkOperationFailureCategory;
  /** Short reason label shown in the table (clickable). */
  reasonLabel: string;
  /** Full human explanation for the details popover. */
  reasonDetail: string;
  channel: string | null;
  rotation: string | null;
  /** staging | pending | production — continuity / channel placements. */
  scheduleSide?: string | null;
  retryable: boolean;
  /** Concise next-action cell text. */
  nextAction: string;
  /** Longer guidance for the details popover. */
  nextActionDetail?: string;
}

export interface BulkOperationResultsSummary {
  /** Past-tense verb for the modal title, e.g. "Archived". */
  actionLabel: string;
  /** Machine key for retry routing (e.g. archive | restore | permanentDelete). */
  operationKey?: string;
  /** Entity noun for copy, e.g. "Song" / "Album". */
  entityNoun: string;
  entityNounPlural: string;
  /** Column headers for identity fields. */
  firstColumnLabel: string;
  secondColumnLabel: string | null;
  showChannelColumns: boolean;
  requested: number;
  succeeded: number;
  failed: number;
  skipped: number;
  retryable: number;
  failures: BulkOperationFailure[];
  completedAt: string;
}

export interface BulkFailureRotationGroup {
  key: string;
  rotation: string | null;
  rows: BulkOperationFailure[];
}

export interface BulkFailureChannelGroup {
  key: string;
  /** staging | pending | production — first in the Schedule → Channel → Rotation hierarchy. */
  scheduleSide: string | null;
  channel: string | null;
  rotations: BulkFailureRotationGroup[];
}

export interface BulkFailureCategoryGroup {
  category: BulkOperationFailureCategory;
  reasonLabel: string;
  count: number;
  channels: BulkFailureChannelGroup[];
  unscoped: BulkOperationFailure[];
}

const CATEGORY_ORDER: BulkOperationFailureCategory[] = [
  "continuity-blocked",
  "channel-cleanup-incomplete",
  "programming-references-remain",
  "dependency",
  "conflict",
  "auth",
  "not-found",
  "network",
  "other",
];

/** Canonical short reason labels — prefer this over stored reasonLabel at display time. */
const CATEGORY_DISPLAY_LABELS: Record<BulkOperationFailureCategory, string> = {
  "continuity-blocked": "Channel Continuity",
  "channel-cleanup-incomplete": "Channel Cleanup Incomplete",
  "programming-references-remain": "Programming References Remain",
  dependency: "Dependency Blocked",
  auth: "Authentication / Permission",
  "not-found": "Not Found",
  conflict: "Conflict",
  network: "Network / Service Error",
  other: "Other Failure",
};

export function categoryDisplayLabel(
  category: BulkOperationFailureCategory,
): string {
  return CATEGORY_DISPLAY_LABELS[category] || CATEGORY_DISPLAY_LABELS.other;
}

export function groupBulkOperationFailures(
  failures: BulkOperationFailure[],
): BulkFailureCategoryGroup[] {
  const byCategory = new Map<BulkOperationFailureCategory, BulkOperationFailure[]>();
  for (const f of failures) {
    const list = byCategory.get(f.category) || [];
    list.push(f);
    byCategory.set(f.category, list);
  }

  const groups: BulkFailureCategoryGroup[] = [];
  for (const cat of CATEGORY_ORDER) {
    const rows = byCategory.get(cat);
    if (!rows || rows.length === 0) continue;

    const byPlacement = new Map<string, BulkOperationFailure[]>();
    const unscoped: BulkOperationFailure[] = [];
    for (const f of rows) {
      if (!f.channel) {
        unscoped.push(f);
        continue;
      }
      // Schedule → Channel hierarchy for section grouping.
      const key = `${f.scheduleSide || ""}::${f.channel}`;
      const list = byPlacement.get(key) || [];
      list.push(f);
      byPlacement.set(key, list);
    }

    const channels: BulkFailureChannelGroup[] = [];
    // Stable scan order: production, pending, staging, then channel name.
    const sideRank = (side: string | null | undefined) => {
      const s = String(side || "").toLowerCase();
      if (s === "production" || s === "live") return 0;
      if (s === "pending") return 1;
      if (s === "staging") return 2;
      return 3;
    };
    const placementEntries = Array.from(byPlacement.entries()).sort((a, b) => {
      const aSide = a[1][0]?.scheduleSide;
      const bSide = b[1][0]?.scheduleSide;
      const rank = sideRank(aSide) - sideRank(bSide);
      if (rank !== 0) return rank;
      return String(a[1][0]?.channel || "").localeCompare(
        String(b[1][0]?.channel || ""),
      );
    });

    for (const [placementKey, channelRows] of placementEntries) {
      const byRot = new Map<string, BulkOperationFailure[]>();
      for (const f of channelRows) {
        const rKey = f.rotation || "—";
        const list = byRot.get(rKey) || [];
        list.push(f);
        byRot.set(rKey, list);
      }
      channels.push({
        key: placementKey,
        scheduleSide: channelRows[0]?.scheduleSide ?? null,
        channel: channelRows[0]?.channel ?? null,
        rotations: Array.from(byRot.entries()).map(([key, rotRows]) => ({
          key,
          rotation: rotRows[0]?.rotation ?? null,
          rows: rotRows,
        })),
      });
    }

    groups.push({
      category: cat,
      reasonLabel: categoryDisplayLabel(cat),
      // Unique entities (same Song may appear under Staging + Production).
      count: new Set(rows.map((r) => r.id)).size,
      channels,
      unscoped,
    });
  }
  return groups;
}

/** Clean, human-readable failure report for clipboard copy. */
export function formatBulkOperationFailureReport(
  summary: BulkOperationResultsSummary,
): string {
  const lines: string[] = [
    `${summary.actionLabel} — operation results`,
    `Entity: ${summary.entityNounPlural}`,
    `Completed: ${new Date(summary.completedAt).toLocaleString()}`,
    "",
    `Requested: ${summary.requested}`,
    `Succeeded: ${summary.succeeded}`,
    `Failed: ${summary.failed}`,
    `Skipped: ${summary.skipped}`,
    `Retryable: ${summary.retryable}`,
    "",
  ];

  const first = summary.firstColumnLabel;
  const second = summary.secondColumnLabel;

  for (const g of groupBulkOperationFailures(summary.failures)) {
    lines.push(`${g.reasonLabel} — ${g.count} ${g.count === 1 ? summary.entityNoun : summary.entityNounPlural}`);
    lines.push("");

    const emitRow = (row: BulkOperationFailure, indent: string) => {
      lines.push(`${indent}${first}: ${row.first || "—"}`);
      if (second) {
        lines.push(`${indent}${second}: ${row.second || "—"}`);
      }
      lines.push(
        `${indent}Reason: ${row.reasonLabel || categoryDisplayLabel(row.category)}`,
      );
      if (summary.showChannelColumns) {
        lines.push(
          `${indent}Schedule: ${row.scheduleSide ? scheduleSideLabel(row.scheduleSide) : "—"}`,
        );
        lines.push(`${indent}Channel: ${row.channel || "—"}`);
        lines.push(`${indent}Rotation: ${row.rotation || "—"}`);
      }
      lines.push(`${indent}Retryable: ${row.retryable ? "Yes" : "No"}`);
      lines.push(`${indent}Next Action: ${row.nextAction}`);
      lines.push("");
    };

    for (const ch of g.channels) {
      lines.push(
        `  Schedule: ${ch.scheduleSide ? scheduleSideLabel(ch.scheduleSide) : "—"}`,
      );
      lines.push(`  Channel: ${ch.channel || "—"}`);
      for (const rot of ch.rotations) {
        lines.push(`    Rotation: ${rot.rotation || "—"} — ${rot.rows.length}`);
        for (const row of rot.rows) emitRow(row, "      ");
      }
    }
    for (const row of g.unscoped) emitRow(row, "  ");
  }

  return lines.join("\n").trim() + "\n";
}

export function buildBulkOperationResultsSummary(args: {
  actionLabel: string;
  operationKey?: string;
  entityNoun: string;
  entityNounPlural: string;
  firstColumnLabel: string;
  secondColumnLabel?: string | null;
  showChannelColumns?: boolean;
  requested: number;
  succeeded: number;
  skipped: number;
  failures: BulkOperationFailure[];
}): BulkOperationResultsSummary {
  const uniqueFailedIds = new Set(args.failures.map((f) => f.id));
  const uniqueRetryableIds = new Set(
    args.failures.filter((f) => f.retryable).map((f) => f.id),
  );
  return {
    actionLabel: args.actionLabel,
    operationKey: args.operationKey,
    entityNoun: args.entityNoun,
    entityNounPlural: args.entityNounPlural,
    firstColumnLabel: args.firstColumnLabel,
    secondColumnLabel: args.secondColumnLabel ?? null,
    showChannelColumns: args.showChannelColumns !== false,
    requested: args.requested,
    succeeded: args.succeeded,
    failed: uniqueFailedIds.size,
    skipped: args.skipped,
    retryable: uniqueRetryableIds.size,
    failures: args.failures,
    completedAt: new Date().toISOString(),
  };
}
